import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { X, Loader2 } from "lucide-react";
import { trackEmailSignup } from "@/lib/analytics";
import { z } from "zod";
import type { Event } from "@/types";
import { format, parseISO } from "date-fns";
import ctaBackground from "@/assets/cta-background.png";

const emailSchema = z.string().email("Please enter a valid email address").max(255);
const phoneSchema = z.string().regex(/^\+?[1-9]\d{6,14}$/, "Please enter a valid phone number");

interface SmsPopupProps {
  currentEvent?: Event | null;
  countdownDate?: Date;
}

const SmsPopup = ({ currentEvent, countdownDate }: SmsPopupProps) => {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });

  // Show popup after 5 seconds if not previously dismissed
  useEffect(() => {
    const dismissed = sessionStorage.getItem("sms_popup_dismissed");
    if (dismissed) return;

    const timer = setTimeout(() => setOpen(true), 5000);
    return () => clearTimeout(timer);
  }, []);

  // Countdown timer
  useEffect(() => {
    if (!countdownDate) return;

    const tick = () => {
      const now = new Date().getTime();
      const target = countdownDate.getTime();
      const diff = Math.max(0, target - now);

      setTimeLeft({
        days: Math.floor(diff / (1000 * 60 * 60 * 24)),
        hours: Math.floor((diff / (1000 * 60 * 60)) % 24),
        minutes: Math.floor((diff / (1000 * 60)) % 60),
        seconds: Math.floor((diff / 1000) % 60),
      });
    };

    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [countdownDate]);

  const handleClose = () => {
    setOpen(false);
    sessionStorage.setItem("sms_popup_dismissed", "true");
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const emailResult = emailSchema.safeParse(email.trim());
    if (!emailResult.success) {
      toast.error(emailResult.error.errors[0].message);
      return;
    }

    const trimmedPhone = phone.trim();
    if (trimmedPhone) {
      const phoneResult = phoneSchema.safeParse(trimmedPhone);
      if (!phoneResult.success) {
        toast.error("Please enter a valid phone number (e.g. +15551234567)");
        return;
      }
    }

    setIsSubmitting(true);

    try {
      const insertData: Record<string, unknown> = {
        email: emailResult.data,
      };
      if (trimmedPhone) {
        insertData.phone = trimmedPhone;
        insertData.sms_opt_in = true;
      }

      const { error } = await supabase
        .from("email_subscribers")
        .insert(insertData as any);

      if (error) {
        if (error.code === "23505") {
          toast.info("You're already subscribed!");
        } else {
          throw error;
        }
      } else {
        trackEmailSignup();
        toast.success("You're in! We'll keep you updated.");
      }

      handleClose();
    } catch (error) {
      console.error("Subscription error:", error);
      toast.error("Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const formattedDate = currentEvent
    ? format(parseISO(currentEvent.date), "MMMM do, yyyy").toUpperCase()
    : null;

  const pad = (n: number) => n.toString().padStart(2, "0");

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) handleClose(); }}>
      <DialogContent
        className="p-0 border-0 overflow-hidden max-w-lg sm:max-w-xl bg-transparent shadow-2xl [&>button]:hidden"
        aria-describedby="sms-popup-description"
      >
        {/* Background image container */}
        <div
          className="relative rounded-lg overflow-hidden"
          style={{ backgroundImage: `url(${ctaBackground})`, backgroundSize: "cover", backgroundPosition: "center" }}
        >
          {/* Overlay for readability */}
          <div className="absolute inset-0 bg-black/60" />

          {/* Close button */}
          <button
            onClick={handleClose}
            className="absolute top-3 right-3 z-20 w-8 h-8 flex items-center justify-center rounded-full bg-white/20 hover:bg-white/30 transition-colors"
            aria-label="Close"
          >
            <X className="w-5 h-5 text-white" />
          </button>

          <div className="relative z-10 p-6 sm:p-8">
            {/* Brand heading */}
            <h3 className="font-heading text-xl sm:text-2xl font-bold text-white text-center tracking-wide mb-1">
              SOLE TO SOLE
            </h3>
            <p className="text-white/60 text-xs text-center tracking-[0.3em] uppercase mb-3">
              Buy · Sell · Trade
            </p>

            {/* Value prop CTA */}
            <p className="text-white text-sm sm:text-base text-center font-semibold mb-1">
              🔥 Get Early Access & Exclusive Deals
            </p>
            <p className="text-white/70 text-xs sm:text-sm text-center mb-5 max-w-sm mx-auto">
              Be the first to know about future events, exclusive promotions, and early-bird ticket drops.
            </p>

            {/* Countdown */}
            {countdownDate && (
              <div className="flex justify-center gap-4 sm:gap-6 mb-4">
                {[
                  { val: timeLeft.days, label: "days" },
                  { val: timeLeft.hours, label: "hours" },
                  { val: timeLeft.minutes, label: "minutes" },
                  { val: timeLeft.seconds, label: "seconds" },
                ].map((item) => (
                  <div key={item.label} className="text-center">
                    <div className="font-heading text-3xl sm:text-4xl font-bold text-white leading-none">
                      {pad(item.val)}
                    </div>
                    <div className="text-white/60 text-[10px] sm:text-xs uppercase tracking-wider mt-1">
                      {item.label}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Event info */}
            {currentEvent && (
              <p className="font-heading text-sm sm:text-base font-bold text-white text-center mb-6 leading-snug">
                NEXT EVENT @ {currentEvent.venue.toUpperCase()}
                <br />
                IS {formattedDate}!
              </p>
            )}

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-3">
              <div className="flex flex-col sm:flex-row gap-2">
                <Input
                  type="email"
                  placeholder="Enter your email address"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="flex-1 bg-white/95 border-0 text-foreground placeholder:text-muted-foreground h-12"
                  maxLength={255}
                  disabled={isSubmitting}
                  required
                />
                <Input
                  type="tel"
                  placeholder="Phone Number"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className="flex-1 bg-white/95 border-0 text-foreground placeholder:text-muted-foreground h-12"
                  maxLength={16}
                  disabled={isSubmitting}
                />
              </div>

              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full h-12 text-base font-heading font-bold tracking-wider bg-primary hover:bg-primary/90 text-primary-foreground"
              >
                {isSubmitting ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  "SUBMIT"
                )}
              </Button>
            </form>

            {/* Compliance text */}
            <p className="text-white/70 text-[10px] sm:text-xs leading-relaxed mt-4">
              By submitting this form, you consent to receive informational (e.g., event updates) and/or marketing texts (e.g., event reminders) from Sole To Sole including texts sent by auto dialer. Consent is not a condition of purchase. Msg & data rates may apply. Msg frequency varies. Unsubscribe at any time by replying STOP or clicking the unsubscribe link (where available).
            </p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SmsPopup;
